import React, { useEffect, useState, useRef, useMemo } from "react";
import { Modal, Button, Form, Badge, Card, Table, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import Menu from '../../comum/Menu/menu.jsx';
import Footer from '../../comum/Footer/index.jsx';
import { api } from '../../services/api.js';
import Foto from "../Components/Foto";
import {
    FaSearch,
    FaUmbrellaBeach,
    FaFilter,
    FaUserCheck,
    FaPrint,
    FaHistory,
    FaEdit,
    FaBan,
    FaCheck,
    FaPlus,
    FaTimes,
    FaBuilding
} from "react-icons/fa";

const fotoCache = new Map();
const dadosUsuarioCache = new Map();

// Helper para obter a chapa logada dinamicamente
const getChapaUsuario = () => localStorage.getItem("chapa") || "";

// Helper para verificar se a operação é referente a férias
const isOperacaoFerias = (item) => {
    if (!item) return false;
    const tipoOp = Number(item.tipooperacao);
    const tipoStr = String(item.tipo || '').toUpperCase();
    return tipoOp === 3 || tipoStr.includes("FÉRIAS") || tipoStr.includes("FERIAS");
};

// Helper para YYYY-MM inicial
const getMesAnoAtualInput = () => {
    const data = new Date();
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    return `${ano}-${mes}`;
};

// Helpers para Formatação de Moeda
const formatarMoedaInput = (valor) => {
    if (valor === undefined || valor === null || valor === "") return "";
    let limpo = String(valor).replace(/\D/g, "");
    if (!limpo) return "";

    let num = (parseInt(limpo, 10) / 100).toFixed(2);
    let partes = num.split(".");
    partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `R$ ${partes.join(",")}`;
};

const converterMoedaParaFloat = (valorFormatado) => {
    if (!valorFormatado) return 0;
    const numLimpo = String(valorFormatado)
        .replace(/R\$\s?/g, "")
        .replace(/\./g, "")
        .replace(",", ".");
    return parseFloat(numLimpo) || 0;
};

// Formata datas YYYY-MM-DD sem fuso
const formatarDataSemFuso = (dataStr) => {
    if (!dataStr) return '--';
    const trechoData = dataStr.substring(0, 10);
    const partes = trechoData.split('-');
    if (partes.length !== 3) return dataStr;
    const [ano, mes, dia] = partes;
    return `${dia}/${mes}/${ano}`;
};

// Miniatura de Foto
const FotoMiniatura = ({ chapa, onFotoCarregada }) => {
    const [foto, setFoto] = useState(null);

    useEffect(() => {
        if (!chapa) return;
        const hoje = new Date().toISOString().slice(0, 10);
        const chave = `ferias_${chapa}_${hoje}`;

        if (fotoCache.has(chave)) {
            const fotoCacheada = fotoCache.get(chave);
            setFoto(fotoCacheada);
            if (onFotoCarregada) onFotoCarregada(chapa, fotoCacheada);
        } else {
            api.get(`/usuario/foto/${chapa}`)
                .then(res => {
                    if (res.data?.foto) {
                        fotoCache.set(chave, res.data.foto);
                        setFoto(res.data.foto);
                        if (onFotoCarregada) onFotoCarregada(chapa, res.data.foto);
                    }
                })
                .catch(() => { });
        }
    }, [chapa]);

    if (!foto) {
        return (
            <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center text-secondary border mx-auto"
                style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}
            >
                👤
            </div>
        );
    }

    return (
        <img
            src={`data:image/jpeg;base64,${foto}`}
            alt=""
            className="rounded-circle border"
            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
        />
    );
};

// Placeholder Foto Card
const FotoPlaceholder = () => (
    <div
        className="rounded-circle me-3 d-flex align-items-center justify-content-center bg-light text-secondary border flex-shrink-0"
        style={{ width: '60px', height: '60px', minWidth: '60px' }}
    >
        <span style={{ fontSize: '1.8rem', color: '#adb5bd' }}>👤</span>
    </div>
);

// Componente Assinante Pendente
const ItemAssinantePendente = ({ chapa, statusAprovacao, nome: nomeProp, funcao: funcaoProp }) => {
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(!nomeProp);

    useEffect(() => {
        let isMounted = true;
        if (nomeProp) {
            setDados({ nome: nomeProp, funcao: funcaoProp });
            setCarregando(false);
            return;
        }

        if (!chapa) return;

        if (dadosUsuarioCache.has(chapa)) {
            setDados(dadosUsuarioCache.get(chapa));
            setCarregando(false);
        } else {
            setCarregando(true);
            api.get(`/usuario/dados/${chapa}`)
                .then(res => {
                    if (isMounted) {
                        const info = Array.isArray(res.data) ? res.data[0] : res.data;
                        if (info) {
                            dadosUsuarioCache.set(chapa, info);
                            setDados(info);
                        }
                    }
                })
                .catch(() => { })
                .finally(() => {
                    if (isMounted) setCarregando(false);
                });
        }
        return () => { isMounted = false; };
    }, [chapa, nomeProp, funcaoProp]);

    const renderBadgeStatus = () => {
        const st = String(statusAprovacao);
        if (st === "1") return <Badge bg="success">Aprovado</Badge>;
        if (st === "2") return <Badge bg="danger">Recusado</Badge>;
        return <Badge bg="warning" text="dark">⏳ Pendente</Badge>;
    };

    return (
        <div className="d-flex align-items-center justify-content-between p-2 mb-1 bg-white rounded border small">
            <div>
                <span className="fw-bold text-dark">{dados?.nome || `Matrícula: ${chapa}`}</span>
                <span className="text-muted ms-2">({chapa})</span>
                {dados?.funcao && <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{dados.funcao}</div>}
            </div>
            <div>
                {carregando ? <Spinner animation="border" size="sm" /> : renderBadgeStatus()}
            </div>
        </div>
    );
};

// Card do Comunicado com COD_OPERACAO
const ComunicadoCard = ({ item, onClick, onImprimir }) => {
    const [foto, setFoto] = useState(null);
    const [carregandoFoto, setCarregandoFoto] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const hoje = new Date().toISOString().slice(0, 10);
        const chave = `${item.chapa}_${hoje}`;

        if (fotoCache.has(chave)) {
            setFoto(fotoCache.get(chave));
            setCarregandoFoto(false);
        } else {
            setCarregandoFoto(true);
            api.get(`/usuario/foto/${item.chapa}`)
                .then(res => {
                    if (isMounted && res.data?.foto) {
                        fotoCache.set(chave, res.data.foto);
                        setFoto(res.data.foto);
                    }
                })
                .catch(() => { })
                .finally(() => {
                    if (isMounted) setCarregandoFoto(false);
                });
        }

        return () => { isMounted = false; };
    }, [item.chapa]);

    const exibirBotaoImprimir = Number(item.status) === 5 && !isOperacaoFerias(item);

    return (
        <Card
            className="shadow-sm h-100 border-start border-4 position-relative overflow-hidden"
            style={{ borderRadius: '8px', borderLeftColor: '#991b1b' }}
        >
            <div className="p-3 cursor-pointer" onClick={() => onClick(item, foto)} style={{ cursor: 'pointer' }}>
                {/* Linha Superior: Badges e Data organizados com wrap para não quebrar a borda */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-1 mb-2">
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                        <Badge className="btn-custom-red text-wrap text-start" style={{ fontSize: '0.7rem', padding: '4px 6px' }}>
                            {item.tipo}
                        </Badge>
                        <span className="badge bg-dark text-white" style={{ fontSize: '0.7rem' }}>
                            #{item.cod_operacao}
                        </span>
                    </div>
                    {item.hora && (
                        <small className="text-muted fw-bold ms-auto" style={{ fontSize: '0.68rem' }}>
                            {new Date(item.hora).toLocaleDateString('pt-BR')}
                        </small>
                    )}
                </div>

                {/* Conteúdo Principal: Foto + Informações do Funcionário */}
                <div className="d-flex align-items-center">
                    {carregandoFoto || !foto ? (
                        <FotoPlaceholder />
                    ) : (
                        <img
                            src={`data:image/jpeg;base64,${foto}`}
                            alt=""
                            className="rounded-circle me-3 flex-shrink-0"
                            style={{ width: '55px', height: '55px', objectFit: 'cover', border: '2px solid #eee' }}
                        />
                    )}
                    <div className="overflow-hidden" style={{ flex: 1, minWidth: 0 }}>
                        <div className="fw-bold text-uppercase text-truncate mb-1" style={{ fontSize: '0.85rem', color: '#212529' }}>
                            {item.nome}
                        </div>
                        <div className="text-muted lh-sm" style={{ fontSize: '0.75rem' }}>
                            <div>Matrícula: <span className="fw-semibold text-dark">{item.chapa}</span></div>
                            <div className="mt-1">
                                <strong>Loja: {item.filial} ➔ <span style={{ color: '#991b1b' }}>{item.lojades}</span></strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {exibirBotaoImprimir && onImprimir && (
                <div className="border-top p-2 bg-light d-flex justify-content-end">
                    <Button
                        variant="outline-dark"
                        size="sm"
                        className="fw-bold py-0 d-flex align-items-center gap-1"
                        style={{ fontSize: '0.75rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onImprimir(item);
                        }}
                    >
                        <FaPrint /> Ficha Impressão
                    </Button>
                </div>
            )}
        </Card>
    );
};

const getStatusInicialPorModo = (modo) => {
    if (modo === "REGIONAL") return "1";
    if (modo === "DIRETOR") return "4";
    return "2";
};

function AprovacaoComunicado() {
    const navigate = useNavigate();

    const permiteDiretor = localStorage.getItem("diretor") === "S";
    const permiteRegional = localStorage.getItem("permiteRegional") === "S";
    let permiteRH = localStorage.getItem("RHDASHBOARD") === "S";

    if (!permiteDiretor && !permiteRegional && !permiteRH) {
        permiteRH = true;
    }

    const [modoNavegacao, setModoNavegacao] = useState(() => {
        if (permiteRH) return "RH";
        if (permiteRegional) return "REGIONAL";
        if (permiteDiretor) return "DIRETOR";
        return "RH";
    });

    const [comunicados, setComunicados] = useState([]);
    const [loading, setLoading] = useState(false);

    const [statusFiltro, setStatusFiltro] = useState(() => getStatusInicialPorModo(modoNavegacao));
    const [tipoOperacaoFiltro, setTipoOperacaoFiltro] = useState("TODOS");

    // Novos Filtros do Front-end
    const [filtroFerias, setFiltroFerias] = useState("TODOS"); // "TODOS", "SOMENTE", "OCULTAR"
    const [buscaTexto, setBuscaTexto] = useState("");

    const requisicaoAtivaRef = useRef(0);

    const [showDetails, setShowDetails] = useState(false);
    const [showAvaliar, setShowAvaliar] = useState(false);
    const [showLog, setShowLog] = useState(false);
    const [showRHAction, setShowRHAction] = useState(false);
    const [showFeriasModal, setShowFeriasModal] = useState(false);

    const [selected, setSelected] = useState(null);
    const [fotoSelected, setFotoSelected] = useState(null);
    const [logs, setLogs] = useState([]);
    const [motivo, setMotivo] = useState("");
    const [fotoSelecionada, setFotoSelecionada] = useState(null);
    const [pendentesAssinatura, setPendentesAssinatura] = useState([]);

    const [rhActionType, setRhActionType] = useState("DIRETORIA");
    const [semDiretor, setSemDiretor] = useState(false);
    const [dataEfetivo, setDataEfetivo] = useState("");
    const [codHorario, setCodHorario] = useState("");
    const [setorNovo, setSetorNovo] = useState("");
    const [funcaoNova, setFuncaoNova] = useState("");
    const [salarioNovo, setSalarioNovo] = useState("");

    const [descHorario, setDescHorario] = useState("");
    const [descSetor, setDescSetor] = useState("");
    const [descFuncao, setDescFuncao] = useState("");

    const [chapaBuscaDiretor, setChapaBuscaDiretor] = useState("");
    const [diretorEncontrado, setDiretorEncontrado] = useState(null);
    const [buscandoDiretor, setBuscandoDiretor] = useState(false);
    const [listaDiretoresSelecionados, setListaDiretoresSelecionados] = useState([]);

    const [mesAnoFerias, setMesAnoFerias] = useState(getMesAnoAtualInput);
    const [relatorioFerias, setRelatorioFerias] = useState([]);
    const [fotosMap, setFotosMap] = useState({});

    const isMobile = window.innerWidth < 768;

    useEffect(() => {
        const novoStatus = getStatusInicialPorModo(modoNavegacao);
        setStatusFiltro(novoStatus);
        setTipoOperacaoFiltro("TODOS");

        const chapaSalva = getChapaUsuario();
        if (!chapaSalva) {
            const timer = setTimeout(() => {
                loadComunicados(novoStatus, modoNavegacao);
            }, 100);
            return () => clearTimeout(timer);
        }

        loadComunicados(novoStatus, modoNavegacao);
    }, [modoNavegacao]);

    const handleTrocaStatusFiltro = (novoStatus) => {
        setStatusFiltro(novoStatus);
        loadComunicados(novoStatus, modoNavegacao);
    };

    const loadComunicados = async (st = statusFiltro, modo = modoNavegacao) => {
        const idRequisicao = ++requisicaoAtivaRef.current;
        setLoading(true);

        try {
            const chapaAtual = getChapaUsuario();
            const url = modo === "DIRETOR"
                ? `/usuario/comunicacao/${st}?chapa=${chapaAtual}`
                : `/usuario/comunicacao/${st}`;

            const response = await api.get(url);

            if (idRequisicao === requisicaoAtivaRef.current) {
                setComunicados(response.data || []);
            }
        } catch (err) {
            console.error("Erro ao carregar comunicados:", err);
            if (idRequisicao === requisicaoAtivaRef.current) {
                setComunicados([]);
            }
        } finally {
            if (idRequisicao === requisicaoAtivaRef.current) {
                setLoading(false);
            }
        }
    };

    // Lógica de Filtragem Direta do Front-End
    const comunicadosFiltrados = useMemo(() => {
        return comunicados.filter(item => {
            // 1. Filtro Tipo Operação
            if (tipoOperacaoFiltro !== "TODOS" && String(item.tipooperacao) !== String(tipoOperacaoFiltro)) {
                return false;
            }

            // 2. Filtro de Férias
            const eFerias = isOperacaoFerias(item);
            if (filtroFerias === "SOMENTE" && !eFerias) return false;
            if (filtroFerias === "OCULTAR" && eFerias) return false;

            // 3. Filtro Busca por Texto (Nome, Chapa, Código)
            if (buscaTexto.trim() !== "") {
                const termo = buscaTexto.toLowerCase().trim();
                const nomeMatch = String(item.nome || "").toLowerCase().includes(termo);
                const chapaMatch = String(item.chapa || "").toLowerCase().includes(termo);
                const codMatch = String(item.cod_operacao || "").toLowerCase().includes(termo);

                if (!nomeMatch && !chapaMatch && !codMatch) return false;
            }

            return true;
        });
    }, [comunicados, tipoOperacaoFiltro, filtroFerias, buscaTexto]);

    const getFotoUsuario = (usuarioParam) => {
        const hoje = new Date().toISOString().slice(0, 10);
        const chave = `log_${usuarioParam}_${hoje}`;
        const foto = fotoCache.get(chave);

        if (!foto) {
            api.get(`/usuario/foto/${usuarioParam}`)
                .then(res => {
                    if (res.data?.foto) {
                        fotoCache.set(chave, res.data.foto);
                        setLogs(prev => [...prev]);
                    }
                })
                .catch(() => { });
        }
        return foto;
    };

    const buscarDescricaoHorario = async (codigo) => {
        if (!codigo || codigo.trim() === "") {
            setDescHorario("");
            return;
        }
        setDescHorario("Buscando...");
        try {
            const res = await api.get(`/rh/vagas/horario/${codigo}`);
            setDescHorario(res.data?.descricao || "Horário não encontrado");
        } catch (err) {
            setDescHorario("Descrição não encontrada");
        }
    };

    const buscarDescricaoSetor = async (codigo) => {
        if (!codigo || codigo.trim() === "") {
            setDescSetor("");
            return;
        }
        setDescSetor("Buscando...");
        try {
            const res = await api.get(`/rh/vagas/setor/${codigo}`);
            setDescSetor(res.data?.descricao || "Setor não encontrado");
        } catch (err) {
            setDescSetor("Descrição não encontrada");
        }
    };

    const buscarDescricaoFuncao = async (codigo) => {
        if (!codigo || codigo.trim() === "") {
            setDescFuncao("");
            return;
        }
        setDescFuncao("Buscando...");
        try {
            const res = await api.get(`/rh/vagas/funcao/${codigo}`);
            setDescFuncao(res.data?.descricao || "Função não encontrada");
        } catch (err) {
            setDescFuncao("Descrição não encontrada");
        }
    };

    const handleOpenDetails = async (item, foto) => {
        setSelected(item);
        setFotoSelected(foto);
        setPendentesAssinatura([]);
        setListaDiretoresSelecionados([]);
        setSemDiretor(false);

        setDataEfetivo(item.dataefetivo ? item.dataefetivo.substring(0, 10) : "");

        const hCod = item.codhorarionovo || item.codhorario || "";
        const sCod = item.setornovo || item.setor || "";
        const fCod = item.funcaonova || item.funcao || "";

        setCodHorario(hCod);
        setDescHorario(item.deschorarionovo || item.deschorario || "");

        setSetorNovo(sCod);
        setDescSetor(item.descsetornovo || item.setor || "");

        setFuncaoNova(fCod);
        setDescFuncao(item.descfuncnova || item.funcao || "");

        const valInicial = item.salarionovo || item.salario || 0;
        const valEmCentavos = Math.round(valInicial * 100);
        setSalarioNovo(formatarMoedaInput(valEmCentavos));

        if (item.status === 3 || item.status === 4) {
            try {
                const resCadeia = await api.get(`/rh/comunicacao/cadeia/${item.cod_operacao}`);
                const cadeiaData = resCadeia.data || [];
                const tipoAlvo = item.status === 3 ? 1 : 2;

                const assinantes = cadeiaData
                    .filter(c => Number(c.tipo) === tipoAlvo)
                    .map(c => ({
                        chapa: c.chapa,
                        nome: c.nome,
                        funcao: c.funcao,
                        statusAprovacao: c.aprovado !== undefined ? String(c.aprovado) : "0"
                    }));

                setPendentesAssinatura(assinantes);

                if (item.status === 4) {
                    const diretoresIniciais = cadeiaData
                        .filter(c => Number(c.tipo) === 2)
                        .map(c => ({
                            chapa: c.chapa,
                            nome: c.nome,
                            funcao: c.funcao
                        }));
                    setListaDiretoresSelecionados(diretoresIniciais);
                }
            } catch (err) {
                console.error("Erro ao buscar cadeia:", err);
            }
        }

        setShowDetails(true);
    };

    const fetchLogs = async (codigo) => {
        setLoading(true);
        try {
            const response = await api.get(`/usuario/comunicacao/log/${codigo}`);
            setLogs(response.data || []);
            setShowLog(true);
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Erro', text: 'Não foi possível carregar os logs.' });
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (statusAcao) => {
        setLoading(true);
        const chapaAtual = getChapaUsuario();

        try {
            if (modoNavegacao === "DIRETOR" || selected.status === 4) {
                await api.post('/rh/comunicacao/diretor/avaliar', {
                    cod_operacao: selected.cod_operacao,
                    chapa: chapaAtual,
                    aprovado: statusAcao === 'N' ? 2 : 1,
                    observacao: motivo
                });
            } else {
                let statusFinal = selected.status === 1 ? (statusAcao === 'N' ? 12 : 2) : 5;
                await api.post('/usuario/comunicacao', {
                    codigo: selected.cod_operacao,
                    status: statusFinal,
                    usuario: chapaAtual,
                    operacao: String(selected.tipooperacao),
                    observacao: motivo,
                    autorizado: statusAcao === 'N' ? "2" : "1"
                });
            }

            setShowAvaliar(false);
            setShowDetails(false);
            setMotivo("");
            Swal.fire({ icon: 'success', title: 'Sucesso', text: 'Operação realizada com sucesso!' });
            loadComunicados(statusFiltro, modoNavegacao);
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Erro', text: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleBuscarDiretor = async () => {
        if (!chapaBuscaDiretor) return;
        setBuscandoDiretor(true);
        setDiretorEncontrado(null);
        try {
            const res = await api.get(`/usuario/dados/${chapaBuscaDiretor}`);
            const dados = Array.isArray(res.data) ? res.data[0] : res.data;

            if (dados && dados.nome) {
                setDiretorEncontrado(dados);
            } else {
                Swal.fire({ icon: 'warning', title: 'Não Encontrado', text: 'Matrícula não localizada no sistema.' });
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Erro', text: 'Erro ao consultar dados.' });
        } finally {
            setBuscandoDiretor(false);
        }
    };

    const handleAdicionarDiretorLista = () => {
        if (!diretorEncontrado) return;
        const jaExiste = listaDiretoresSelecionados.some(d => d.chapa === diretorEncontrado.chapa);
        if (jaExiste) {
            Swal.fire({ icon: 'info', title: 'Atenção', text: 'Este diretor já foi adicionado na lista.' });
            return;
        }

        setListaDiretoresSelecionados(prev => [...prev, diretorEncontrado]);
        setDiretorEncontrado(null);
        setChapaBuscaDiretor("");
    };

    const handleRemoverDiretorLista = (chapaRemover) => {
        setListaDiretoresSelecionados(prev => prev.filter(d => d.chapa !== chapaRemover));
    };

    const handleRHSubmit = async () => {
        if (selected?.status === 2 && !semDiretor && listaDiretoresSelecionados.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Atenção',
                text: 'Para prosseguir, marque a opção "Sem necessidade de Diretor" ou adicione ao menos um diretor na lista.'
            });
            return;
        }

        setLoading(true);
        const chapaAtual = getChapaUsuario();

        try {
            if (selected?.status === 5) {
                await api.post('/usuario/comunicacao', {
                    codigo: selected.cod_operacao,
                    status: 6,
                    usuario: chapaAtual,
                    operacao: String(selected.tipooperacao),
                    observacao: motivo || "Encaminhado para ciência do funcionário pelo RH.",
                    autorizado: "1"
                });
            } else {
                const salarioFloat = converterMoedaParaFloat(salarioNovo);
                const statusDestinoFinal = semDiretor ? 5 : 4;

                if (selected?.status === 2 || selected?.status === 4) {
                    await api.put('/rh/comunicacao/editar-validar', {
                        cod_operacao: selected.cod_operacao,
                        dataefetivo: dataEfetivo,
                        codhorario: codHorario,
                        setornovo: setorNovo,
                        funcaonova: funcaoNova,
                        salarionovo: salarioFloat,
                        statusdestino: statusDestinoFinal,
                        usuario: chapaAtual,
                        observacao: motivo || (semDiretor ? "Validação RH (Sem necessidade de Diretor)" : "")
                    });
                }

                if (!semDiretor && rhActionType === "DIRETORIA") {
                    const diretoresArray = listaDiretoresSelecionados.map(d => String(d.chapa));

                    await api.post('/rh/comunicacao/diretores-ordem', {
                        cod_operacao: selected.cod_operacao,
                        solicitante: chapaAtual,
                        diretores: diretoresArray
                    });
                }
            }

            setShowRHAction(false);
            setShowDetails(false);
            setMotivo("");
            setListaDiretoresSelecionados([]);
            Swal.fire({ icon: 'success', title: 'Sucesso', text: 'Operação realizada com sucesso!' });
            loadComunicados(statusFiltro, modoNavegacao);
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Erro no Processamento', text: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleImprimirFicha = async (itemParam) => {
        const item = itemParam || selected;
        if (!item || isOperacaoFerias(item)) return;

        setLoading(true);
        try {
            const resLog = await api.get(`/usuario/comunicacao/log/${item.cod_operacao}`);
            const logsComunicado = resLog.data || [];

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                Swal.fire({ icon: 'warning', title: 'Bloqueador de Pop-up', text: 'Permita pop-ups para imprimir.' });
                return;
            }

            const linhasLogsHtml = logsComunicado.length > 0 ? logsComunicado.map(l => `
                <tr>
                    <td>${l.hora ? new Date(l.hora).toLocaleString('pt-BR') : ''}</td>
                    <td>${l.nome || ''} (${l.usuario || ''})</td>
                    <td><strong>${l.descstatus || ''}</strong></td>
                    <td>${l.autorizado || ''}</td>
                    <td>${l.observacaolog || '-'}</td>
                </tr>
            `).join('') : `<tr><td colspan="5" style="text-align: center;">Nenhum histórico registrado.</td></tr>`;

            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Ficha Cadastral - Comunicado #${item.cod_operacao}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; color: #333; }
                        .header { text-align: center; border-bottom: 2px solid #991b1b; padding-bottom: 10px; margin-bottom: 15px; }
                        .header h2 { margin: 0; color: #991b1b; font-size: 16px; }
                        .header p { margin: 5px 0 0 0; font-weight: bold; color: #555; }
                        .section-title { font-weight: bold; background: #e9ecef; padding: 6px; margin-top: 15px; margin-bottom: 5px; border-left: 4px solid #991b1b; text-transform: uppercase; font-size: 11px; }
                        .grid { display: flex; flex-wrap: wrap; border: 1px solid #ccc; padding: 8px; border-radius: 4px; }
                        .col-6 { width: 50%; box-sizing: border-box; padding: 4px; }
                        .col-12 { width: 100%; box-sizing: border-box; padding: 4px; }
                        .label { font-size: 9px; color: #6c757d; text-transform: uppercase; display: block; }
                        .val { font-size: 11px; font-weight: bold; }
                        .val-danger { font-size: 11px; font-weight: bold; color: #991b1b; }
                        table { width: 100%; border-collapse: collapse; margin-top: 5px; font-size: 10px; }
                        th, td { border: 1px solid #ccc; padding: 5px; text-align: left; }
                        th { background-color: #f8f9fa; font-weight: bold; text-transform: uppercase; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>FICHA DE MOVIMENTAÇÃO CADASTRAL DE FUNCIONÁRIO</h2>
                        <p>COMUNICADO #${item.cod_operacao} - TIPO: ${item.tipo || 'MOVIMENTAÇÃO'}</p>
                    </div>
                    
                    <div class="grid">
                        <div class="col-6"><span class="label">Funcionário</span><span class="val">${item.nome} (${item.chapa})</span></div>
                        <div class="col-6"><span class="label">Data Solicitação</span><span class="val">${item.hora ? new Date(item.hora).toLocaleString('pt-BR') : ''}</span></div>
                        <div class="col-6"><span class="label">Loja Atual</span><span class="val">${item.filial}</span></div>
                        <div class="col-6"><span class="label">Loja Destino</span><span class="val val-danger">${item.lojades || '--'}</span></div>
                        <div class="col-6"><span class="label">Setor Atual</span><span class="val">${item.setor || '--'}</span></div>
                        <div class="col-6"><span class="label">Novo Setor</span><span class="val val-danger">${item.descsetornovo || item.setornovo || '--'}</span></div>
                        <div class="col-6"><span class="label">Função Atual</span><span class="val">${item.funcao || '--'}</span></div>
                        <div class="col-6"><span class="label">Nova Função</span><span class="val val-danger">${item.descfuncnova || item.funcaonova || '--'}</span></div>
                        <div class="col-6"><span class="label">Salário Atual</span><span class="val">${item.salario ? Number(item.salario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00'}</span></div>
                        <div class="col-6"><span class="label">Novo Salário</span><span class="val val-danger">${item.salarionovo ? Number(item.salarionovo).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00'}</span></div>
                        <div class="col-6"><span class="label">Horário Atual</span><span class="val">${item.deschorario || '--'}</span></div>
                        <div class="col-6"><span class="label">Novo Horário</span><span class="val val-danger">${item.deschorarionovo || item.codhorarionovo || '--'}</span></div>
                        <div class="col-12" style="border-top: 1px dashed #ccc; margin-top: 5px; padding-top: 5px;">
                            <span class="label">Data Efetiva da Mudança</span><span class="val val-danger">${formatarDataSemFuso(item.dataefetivo)}</span>
                        </div>
                    </div>

                    <div class="section-title">Histórico / Movimentações (Logs)</div>
                    <table>
                        <thead>
                            <tr>
                                <th style="width: 110px;">Data/Hora</th>
                                <th>Usuário</th>
                                <th>Status</th>
                                <th style="width: 70px;">Autorização</th>
                                <th>Observação</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${linhasLogsHtml}
                        </tbody>
                    </table>

                    <script>
                        window.onload = function() { window.print(); window.close(); }
                    </script>
                </body>
                </html>
            `;
            printWindow.document.write(htmlContent);
            printWindow.document.close();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Erro na Impressão', text: 'Não foi possível gerar a ficha e os logs.' });
        } finally {
            setLoading(false);
        }
    };

    const handleConsultarFerias = async () => {
        if (!mesAnoFerias) return;
        const [ano, mes] = mesAnoFerias.split('-');
        const mesAnoParam = `${mes}/${ano}`;

        setLoading(true);
        try {
            const res = await api.get(`/rh/comunicacao/relatorio-ferias?mesano=${encodeURIComponent(mesAnoParam)}`);
            setRelatorioFerias(res.data || []);
        } catch (err) {
            setRelatorioFerias([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFotoCarregada = (chapa, b64Foto) => {
        setFotosMap(prev => ({ ...prev, [chapa]: b64Foto }));
    };

    const handleImprimirFerias = () => {
        if (!relatorioFerias || relatorioFerias.length === 0) return;
        const [ano, mes] = mesAnoFerias.split('-');

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            Swal.fire({ icon: 'warning', title: 'Bloqueador de Pop-up', text: 'Permita pop-ups para imprimir.' });
            return;
        }

        const linhasTabela = relatorioFerias.map((item, index) => `
            <tr>
                <td style="text-align: center;">${index + 1}</td>
                <td>${item.chapa}</td>
                <td><strong>${item.nome}</strong></td>
                <td>${item.setor || '-'}</td>
                <td>${item.funcao || '-'}</td>
                <td style="text-align: center;">${item.data_inicio_ferias || '-'}</td>
                <td style="text-align: center;">${item.status || '-'}</td>
                <td>${item.observacao || '-'}</td>
            </tr>
        `).join('');

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Relatório de Férias - ${mes}/${ano}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; color: #333; }
                    .header { text-align: center; border-bottom: 2px solid #991b1b; padding-bottom: 10px; margin-bottom: 15px; }
                    .header h2 { margin: 0; color: #991b1b; font-size: 16px; }
                    .header p { margin: 5px 0 0 0; font-weight: bold; color: #555; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 10px; }
                    th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
                    th { background-color: #f8f9fa; font-weight: bold; text-transform: uppercase; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h2>RELATÓRIO EXCLUSIVO DE FÉRIAS</h2>
                    <p>MÊS/ANO DE REFERÊNCIA: ${mes}/${ano}</p>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th style="width: 30px; text-align: center;">#</th>
                            <th style="width: 60px;">Chapa</th>
                            <th>Nome</th>
                            <th>Setor</th>
                            <th>Função</th>
                            <th style="width: 80px; text-align: center;">Início Férias</th>
                            <th style="width: 60px; text-align: center;">Status</th>
                            <th>Observação</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${linhasTabela}
                    </tbody>
                </table>
                <script>
                    window.onload = function() { window.print(); window.close(); }
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    const formatMoeda = (valor) => valor ? valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <style>{`
                .btn-custom-red {
                    background-color: #991b1b !important;
                    border-color: #991b1b !important;
                    color: #fff !important;
                }
                .btn-custom-red:hover {
                    background-color: #7f1d1d !important;
                    border-color: #7f1d1d !important;
                }
                .btn-outline-custom-red {
                    color: #991b1b !important;
                    border-color: #991b1b !important;
                }
                .btn-outline-custom-red:hover, .btn-outline-custom-red.active {
                    background-color: #991b1b !important;
                    color: #fff !important;
                }
                .card-header-red {
                    background-color: #991b1b !important;
                    color: #fff !important;
                }
            `}</style>

            <Menu modulo="ARBOR" />

            <div className="container-fluid px-4 pb-5 mb-5" style={{ marginTop: '75px', minHeight: '80vh' }}>
                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3 flex-wrap gap-3 bg-white p-3 rounded shadow-sm">
                    <h4 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ color: '#991b1b' }}>
                        <FaUserCheck /> Avaliação de Comunicado
                    </h4>

                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2 bg-light p-2 rounded border">
                            <Form.Label className="m-0 text-dark small fw-bold text-nowrap">Tipo Operação:</Form.Label>
                            <Form.Select
                                size="sm"
                                style={{ width: '160px', fontWeight: 'bold' }}
                                value={tipoOperacaoFiltro}
                                onChange={e => setTipoOperacaoFiltro(e.target.value)}
                            >
                                <option value="TODOS">Todos</option>
                                <option value="1">1 - Transferência</option>
                                <option value="2">2 - Alteração Salarial</option>
                                <option value="3">3 - Férias</option>
                            </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 bg-light p-2 rounded border">
                            <Form.Label className="m-0 text-dark small fw-bold text-nowrap">Navegar como:</Form.Label>
                            <Form.Select
                                size="sm"
                                style={{ width: '180px', fontWeight: 'bold' }}
                                value={modoNavegacao}
                                onChange={e => setModoNavegacao(e.target.value)}
                            >
                                {permiteRH && <option value="RH">Visão RH (Geral)</option>}
                                {permiteRegional && <option value="REGIONAL">Visão Regional</option>}
                                {permiteDiretor && <option value="DIRETOR">Visão Diretoria</option>}
                            </Form.Select>
                        </div>

                        {modoNavegacao === "RH" && (
                            <Button variant="outline-success" size="sm" className="fw-bold d-flex align-items-center gap-1" onClick={() => setShowFeriasModal(true)}>
                                <FaUmbrellaBeach /> Relatório Férias
                            </Button>
                        )}
                    </div>
                </div>

                {/* BARRA DE FILTROS DO FRONT-END */}
                <div className="card shadow-sm border-0 mb-4 bg-white">
                    <div className="card-body py-2">
                        <Row className="align-items-center g-2">
                            <Col md={6} sm={12}>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-light text-muted">
                                        <FaSearch />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar por Nome, Chapa/Matrícula ou Nº Operação..."
                                        value={buscaTexto}
                                        onChange={e => setBuscaTexto(e.target.value)}
                                    />
                                    {buscaTexto && (
                                        <Button variant="outline-secondary" onClick={() => setBuscaTexto('')}>
                                            <FaTimes />
                                        </Button>
                                    )}
                                </div>
                            </Col>

                            <Col md={6} sm={12} className="d-flex justify-content-md-end align-items-center gap-2">
                                <Form.Label className="m-0 text-muted small fw-bold text-nowrap d-flex align-items-center gap-1">
                                    <FaFilter /> Filtro Férias:
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    style={{ maxWidth: '180px', fontWeight: 'bold' }}
                                    value={filtroFerias}
                                    onChange={e => setFiltroFerias(e.target.value)}
                                >
                                    <option value="TODOS">Todos os Tipos</option>
                                    <option value="SOMENTE">Somente Férias</option>
                                    <option value="OCULTAR">Ocultar Férias</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </div>
                </div>

                {modoNavegacao === "RH" && (
                    <Row className="mb-4">
                        <Col md={12} className="d-flex gap-2 flex-wrap">
                            <Button className={statusFiltro === "1" ? "btn-custom-red" : "btn-outline-custom-red"} size="sm" onClick={() => handleTrocaStatusFiltro("1")}>1- Regional</Button>
                            <Button className={statusFiltro === "2" ? "btn-custom-red" : "btn-outline-custom-red"} size="sm" onClick={() => handleTrocaStatusFiltro("2")}>2- RH Validar</Button>
                            <Button className={statusFiltro === "3" ? "btn-custom-red" : "btn-outline-custom-red"} size="sm" onClick={() => handleTrocaStatusFiltro("3")}>3- Aval. Dependentes</Button>
                            <Button className={statusFiltro === "4" ? "btn-custom-red" : "btn-outline-custom-red"} size="sm" onClick={() => handleTrocaStatusFiltro("4")}>4- Diretoria</Button>
                            <Button className={statusFiltro === "5" ? "btn-custom-red" : "btn-outline-custom-red"} size="sm" onClick={() => handleTrocaStatusFiltro("5")}>5- Lançamento RH</Button>
                            <Button className={statusFiltro === "6" ? "btn-custom-red" : "btn-outline-custom-red"} size="sm" onClick={() => handleTrocaStatusFiltro("6")}>6- Ciência Func.</Button>
                            <Button variant={statusFiltro === "10" ? "success" : "outline-success"} size="sm" onClick={() => handleTrocaStatusFiltro("10")}>10- Finalizados</Button>
                        </Col>
                    </Row>
                )}

                <Row className="g-3 w-100 m-0">
                    {comunicadosFiltrados.length > 0 ? (
                        comunicadosFiltrados.map(item => (
                            <Col xl={4} lg={4} md={6} sm={12} key={item.cod_operacao} className="px-2">
                                <ComunicadoCard item={item} onClick={handleOpenDetails} onImprimir={handleImprimirFicha} />
                            </Col>
                        ))
                    ) : (
                        <Col md={12} className="text-center my-5 text-muted">
                            <h5>Nenhuma pendência encontrada para esta visão e filtros selecionados.</h5>
                        </Col>
                    )}
                </Row>
            </div>

            {/* Modal de Detalhes */}
            <Modal show={showDetails} onHide={() => setShowDetails(false)} fullscreen="sm-down" size="lg" centered>
                <div className="d-flex flex-column bg-white w-100" style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
                    <Modal.Header closeButton className="card-header-red flex-shrink-0 px-3 py-2">
                        <Modal.Title className="fs-6 fw-bold text-truncate">
                            Avaliação de Comunicado <span className="text-warning">#{selected?.cod_operacao}</span>
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body className="p-3" style={{ flex: '1 1 auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '90px' }}>
                        {selected && (
                            <div className="row m-0">
                                <div className="col-md-4 text-center border-end pe-md-3 mb-3 mb-md-0">
                                    {fotoSelected ? (
                                        <img
                                            src={`data:image/jpeg;base64,${fotoSelected}`}
                                            className="img-fluid rounded shadow-sm mb-2"
                                            alt="User"
                                            style={{ cursor: 'pointer', width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '6px' }}
                                            onClick={() => setFotoSelecionada({ foto: `data:image/jpeg;base64,${fotoSelected}`, nome: selected?.nome, loja: "Loja: " + selected?.filial })}
                                        />
                                    ) : (
                                        <div className="my-2 text-secondary">
                                            <span style={{ fontSize: '3rem' }}>👤</span>
                                        </div>
                                    )}
                                    <h6 className="fw-bold mb-1 text-uppercase" style={{ fontSize: '0.85rem' }}>{selected.nome}</h6>

                                    <div className="mb-2">
                                        <Badge bg="info" className="text-dark fw-bold px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                            Tipo Operação: {selected.tipo || selected.tipooperacao}
                                        </Badge>
                                    </div>

                                    <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>Solicitado em: {selected.hora ? new Date(selected.hora).toLocaleString('pt-BR') : ''}</p>
                                    <Button variant="outline-secondary" size="sm" className="w-100 mb-2 py-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '0.8rem' }} onClick={() => fetchLogs(selected.cod_operacao)}>
                                        <FaHistory /> VER LOGS
                                    </Button>

                                    {Number(selected.status) === 5 && !isOperacaoFerias(selected) && (
                                        <Button variant="dark" size="sm" className="w-100 fw-bold mb-2 py-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '0.8rem' }} onClick={() => handleImprimirFicha(selected)}>
                                            <FaPrint /> IMPRIMIR FICHA
                                        </Button>
                                    )}
                                </div>

                                <div className="col-md-8 ps-md-3">
                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Loja Atual</label>
                                            <div className="fw-bold small">{selected.filial}</div>
                                        </div>
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Loja Destino</label>
                                            <div className="fw-bold small" style={{ color: '#991b1b' }}>{selected.lojades}</div>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Setor Atual</label>
                                            <div className="small">{selected.setor}</div>
                                        </div>
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Novo Setor</label>
                                            <div className="small fw-bold" style={{ color: '#991b1b' }}>{selected.descsetornovo || selected.setor || '--'}</div>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Função Atual</label>
                                            <div className="small">{selected.funcao}</div>
                                        </div>
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Nova Função</label>
                                            <div className="small fw-bold" style={{ color: '#991b1b' }}>{selected.descfuncnova || selected.funcao || '--'}</div>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Salário Atual</label>
                                            <div className="small">{formatMoeda(selected.salario)}</div>
                                        </div>
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Novo Salário</label>
                                            <div className="small fw-bold" style={{ color: '#991b1b' }}>{formatMoeda(selected.salarionovo)}</div>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Horário Atual</label>
                                            <div className="small text-muted">{selected.deschorario || '--'}</div>
                                        </div>
                                        <div className="col-6">
                                            <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Novo Horário</label>
                                            <div className="small fw-bold" style={{ color: '#991b1b' }}>{selected.deschorarionovo || selected.codhorarionovo || '--'}</div>
                                        </div>
                                    </div>

                                    <div className="p-2 bg-light border rounded mb-2">
                                        <label className="text-muted small d-block" style={{ fontSize: '0.7rem' }}>Data Efetiva da Mudança:</label>
                                        <div className="fw-bold small" style={{ color: '#991b1b' }}>
                                            {formatarDataSemFuso(selected.dataefetivo)}
                                        </div>
                                    </div>

                                    {(selected?.status === 3 || selected?.status === 4) && (
                                        <div className="p-2 bg-light border border-warning rounded mt-2">
                                            <label className="fw-bold text-dark small d-block mb-1" style={{ fontSize: '0.75rem' }}>
                                                📋 Cadeia de Assinaturas:
                                            </label>
                                            {pendentesAssinatura.length > 0 ? (
                                                pendentesAssinatura.map((item, idx) => (
                                                    <ItemAssinantePendente
                                                        key={idx}
                                                        chapa={item.chapa}
                                                        nome={item.nome}
                                                        funcao={item.funcao}
                                                        statusAprovacao={item.statusAprovacao}
                                                    />
                                                ))
                                            ) : (
                                                <div className="text-muted small italic">
                                                    Nenhum assinante cadastrado nesta cadeia...
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer
                        className="bg-light border-top w-100 p-2 d-flex justify-content-between align-items-center"
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1055,
                            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                            backgroundColor: '#f8f9fa'
                        }}
                    >
                        <Button variant="secondary" size="sm" onClick={() => setShowDetails(false)}>
                            Fechar
                        </Button>

                        {modoNavegacao === "RH" ? (
                            (selected?.status === 2 || selected?.status === 5) ? (
                                <Button className="btn-custom-red size-sm px-3 fw-bold d-flex align-items-center gap-1" onClick={() => { setRhActionType(selected?.status === 2 ? "DIRETORIA" : "CIENCIA"); setShowRHAction(true); }}>
                                    <FaEdit /> EDITAR / AVALIAR RH
                                </Button>
                            ) : selected?.status === 4 ? (
                                <Button
                                    variant="warning"
                                    size="sm"
                                    className="px-3 fw-bold text-dark d-flex align-items-center gap-1"
                                    onClick={() => {
                                        setRhActionType("DIRETORIA");
                                        if (pendentesAssinatura.length > 0) {
                                            setListaDiretoresSelecionados(
                                                pendentesAssinatura.map(p => ({
                                                    chapa: p.chapa,
                                                    nome: p.nome,
                                                    funcao: p.funcao
                                                }))
                                            );
                                        }
                                        setShowRHAction(true);
                                    }}
                                >
                                    <FaEdit /> ALTERAR / REENVIAR DIRETORES
                                </Button>
                            ) : (
                                <Badge bg="secondary" className="p-2" style={{ fontSize: '0.75rem' }}>
                                    ⏳ Aguardando aprovação (Status {selected?.status})
                                </Badge>
                            )
                        ) : (
                            <Button
                                variant="success"
                                size="sm"
                                className="px-4 fw-bold"
                                onClick={() => setShowAvaliar(true)}
                            >
                                AVALIAR OPERAÇÃO
                            </Button>
                        )}
                    </Modal.Footer>
                </div>
            </Modal>

            {/* Modal de RH */}
            <Modal show={showRHAction} onHide={() => setShowRHAction(false)} fullscreen="sm-down" centered size="lg">
                <div className="d-flex flex-column bg-white w-100" style={{ height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
                    <Modal.Header closeButton className="card-header-red flex-shrink-0 px-3 py-2">
                        <Modal.Title className="fs-6 fw-bold">
                            {selected?.status === 5
                                ? "Encaminhar para Ciência do Funcionário"
                                : selected?.status === 4
                                    ? "Alterar / Definir Diretores Avaliadores"
                                    : "Ação e Edição de Dados pelo RH"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-3" style={{ flex: '1 1 auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '90px' }}>
                        <Form>
                            {selected?.status === 2 && (
                                <>
                                    <Row className="mb-2">
                                        <Col md={6} className="mb-2 mb-md-0">
                                            <Form.Label className="fw-bold small">A partir de (Data Efetiva):</Form.Label>
                                            <Form.Control type="date" size="sm" value={dataEfetivo} onChange={e => setDataEfetivo(e.target.value)} />
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label className="fw-bold small">Novo Horário (Cód):</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={codHorario}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setCodHorario(val);
                                                    buscarDescricaoHorario(val);
                                                }}
                                                placeholder="Ex: 001"
                                            />
                                            {descHorario && <small className="text-muted d-block mt-1">📝 Descrição: <strong>{descHorario}</strong></small>}
                                        </Col>
                                    </Row>

                                    <Row className="mb-2">
                                        <Col md={6} className="mb-2 mb-md-0">
                                            <Form.Label className="fw-bold small">Novo Setor (Cód):</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={setorNovo}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setSetorNovo(val);
                                                    buscarDescricaoSetor(val);
                                                }}
                                                placeholder="Ex: 003.52.07"
                                            />
                                            {descSetor && <small className="text-muted d-block mt-1">📝 Descrição: <strong>{descSetor}</strong></small>}
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label className="fw-bold small">Nova Função (Cód):</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={funcaoNova}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setFuncaoNova(val);
                                                    buscarDescricaoFuncao(val);
                                                }}
                                                placeholder="Ex: Operador"
                                            />
                                            {descFuncao && <small className="text-muted d-block mt-1">📝 Descrição: <strong>{descFuncao}</strong></small>}
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={6} className="mb-2 mb-md-0">
                                            <Form.Label className="fw-bold small">Novo Salário (R$):</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                placeholder="R$ 0,00"
                                                value={salarioNovo}
                                                onChange={e => setSalarioNovo(formatarMoedaInput(e.target.value))}
                                            />
                                        </Col>
                                        <Col md={6} className="d-flex align-items-center">
                                            <Form.Check
                                                type="checkbox"
                                                id="chk-sem-diretor"
                                                label={<strong className="text-danger small">Sem necessidade de Diretor (Avançar direto para Status 5)</strong>}
                                                checked={semDiretor}
                                                onChange={e => {
                                                    const checked = e.target.checked;
                                                    setSemDiretor(checked);
                                                    if (checked) {
                                                        setListaDiretoresSelecionados([]);
                                                    }
                                                }}
                                                className="mt-2"
                                            />
                                        </Col>
                                    </Row>
                                    <hr />
                                </>
                            )}

                            {(selected?.status === 2 || selected?.status === 4) && rhActionType === "DIRETORIA" && !semDiretor && (
                                <div className="p-3 bg-light border rounded mb-3">
                                    <Form.Label className="fw-bold small text-danger mb-2">Adicionar Diretor Avaliador:</Form.Label>
                                    <div className="d-flex gap-2 mb-2">
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            placeholder="Digite a matrícula do diretor"
                                            value={chapaBuscaDiretor}
                                            onChange={e => setChapaBuscaDiretor(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleBuscarDiretor())}
                                        />
                                        <Button className="btn-custom-red" size="sm" onClick={handleBuscarDiretor} disabled={buscandoDiretor || !chapaBuscaDiretor}>
                                            {buscandoDiretor ? <Spinner animation="border" size="sm" /> : <><FaSearch /> Buscar</>}
                                        </Button>
                                    </div>

                                    {diretorEncontrado && (
                                        <div className="p-2 border rounded bg-white d-flex align-items-center justify-content-between mb-3">
                                            <div>
                                                <div className="fw-bold text-dark">{diretorEncontrado.nome}</div>
                                                <div className="text-muted small">Matrícula: {diretorEncontrado.chapa} | Cargo: {diretorEncontrado.funcao || 'Diretoria'}</div>
                                            </div>
                                            <Button variant="success" size="sm" onClick={handleAdicionarDiretorLista}>
                                                <FaPlus /> Incluir
                                            </Button>
                                        </div>
                                    )}

                                    <Form.Label className="fw-bold small text-dark mt-2 mb-1">
                                        Lista de Diretores ({listaDiretoresSelecionados.length}):
                                    </Form.Label>
                                    {listaDiretoresSelecionados.length > 0 ? (
                                        <div className="d-flex flex-column gap-1">
                                            {listaDiretoresSelecionados.map((item, idx) => (
                                                <div key={idx} className="d-flex align-items-center justify-content-between p-2 bg-white border rounded">
                                                    <div>
                                                        <strong>{item.nome}</strong>
                                                        <span className="text-muted ms-2 small">({item.chapa})</span>
                                                    </div>
                                                    <div>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="py-0 px-2"
                                                            onClick={() => handleRemoverDiretorLista(item.chapa)}
                                                            title="Remover"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-muted small italic">Nenhum diretor adicionado ainda.</div>
                                    )}
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold small">Observações / Motivo:</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={motivo}
                                    onChange={e => setMotivo(e.target.value)}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>

                    <Modal.Footer
                        className="bg-light border-top w-100 p-2 d-flex justify-content-between align-items-center"
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1055,
                            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                            backgroundColor: '#f8f9fa'
                        }}
                    >
                        <Button variant="secondary" size="sm" onClick={() => setShowRHAction(false)}>Cancelar</Button>
                        <Button variant="success" size="sm" className="px-4 fw-bold" onClick={handleRHSubmit} disabled={loading}>
                            CONFIRMAR E SALVAR
                        </Button>
                    </Modal.Footer>
                </div>
            </Modal>

            {/* Modal LOGS */}
            <Modal show={showLog} onHide={() => setShowLog(false)} size="lg" centered>
                <Modal.Header closeButton className="card-header-red">
                    <Modal.Title className="fs-6 fw-bold">Histórico de Operações</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto', padding: isMobile ? '10px' : '16px' }}>
                    {!isMobile ? (
                        <Table striped hover responsive size="sm">
                            <thead className="table-secondary">
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Usuário</th>
                                    <th>Status</th>
                                    <th>Autorização</th>
                                    <th>Obs</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: '0.85rem' }}>
                                {logs.map((l, index) => {
                                    const fotoUser = getFotoUsuario(l.usuario);
                                    return (
                                        <tr key={index}>
                                            <td>{l.hora ? new Date(l.hora).toLocaleString('pt-BR') : ''}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {fotoUser ? (
                                                        <img src={`data:image/jpeg;base64,${fotoUser}`} alt="" className="rounded-circle" style={{ width: 32, height: 32, objectFit: 'cover' }} />
                                                    ) : (
                                                        <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, fontSize: '0.7rem' }}>👤</div>
                                                    )}
                                                    <div>{l.nome}<br /><small className="text-muted">{l.usuario}</small></div>
                                                </div>
                                            </td>
                                            <td><Badge bg="info">{l.descstatus}</Badge></td>
                                            <td>{l.autorizado}</td>
                                            <td>{l.observacaolog || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    ) : (
                        <div>
                            {logs.map((l, index) => {
                                const fotoUser = getFotoUsuario(l.usuario);
                                return (
                                    <Card key={index} className="mb-2 shadow-sm border-0">
                                        <Card.Body style={{ padding: '10px' }}>
                                            <div className="d-flex align-items-start gap-2">
                                                {fotoUser ? (
                                                    <img src={`data:image/jpeg;base64,${fotoUser}`} alt="" className="rounded-circle" style={{ width: 48, height: 48, objectFit: 'cover' }} />
                                                ) : (
                                                    <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>👤</div>
                                                )}
                                                <div style={{ flex: 1, fontSize: '0.8rem' }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <Badge bg="info">{l.descstatus}</Badge>
                                                        <small className="text-muted">{l.hora ? new Date(l.hora).toLocaleString('pt-BR') : ''}</small>
                                                    </div>
                                                    <div className="fw-bold">{l.nome}</div>
                                                    <div className="text-muted mb-2">{l.usuario}</div>
                                                    <div><strong>Autorização:</strong> {l.autorizado}</div>
                                                    <div><strong>Obs:</strong> {l.observacaolog || '-'}</div>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal Decisão */}
            <Modal show={showAvaliar} onHide={() => setShowAvaliar(false)} centered>
                <Modal.Header closeButton className="card-header-red">
                    <Modal.Title className="fs-6 fw-bold">Concluir Avaliação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label className="fw-bold small">Observações / Motivo:</Form.Label>
                        <Form.Control as="textarea" rows={3} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button variant="danger" className="px-4 d-flex align-items-center gap-1" onClick={() => handleAction('N')} disabled={!motivo || loading}>
                        <FaBan /> NEGAR
                    </Button>
                    <Button variant="success" className="px-4 d-flex align-items-center gap-1" onClick={() => handleAction('A')} disabled={!motivo || loading}>
                        <FaCheck /> APROVAR
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Relatório de Férias */}
            <Modal show={showFeriasModal} onHide={() => setShowFeriasModal(false)} size="xl" centered>
                <Modal.Header closeButton className="card-header-red">
                    <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2">
                        <FaUmbrellaBeach /> Relatório Exclusivo de Férias
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <div className="bg-light p-3 rounded border mb-3">
                        <Row className="align-items-end g-2">
                            <Col md={8} sm={8}>
                                <Form.Group className="mb-0">
                                    <Form.Label className="fw-bold small text-muted mb-1">Mês e Ano de Referência:</Form.Label>
                                    <Form.Control
                                        type="month"
                                        value={mesAnoFerias}
                                        onChange={e => setMesAnoFerias(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4} sm={4}>
                                <Button className="btn-custom-red w-100 fw-bold d-flex align-items-center justify-content-center gap-1" onClick={handleConsultarFerias} disabled={loading}>
                                    <FaSearch /> Consultar
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                        <Table striped bordered hover responsive size="sm" className="m-0 align-middle">
                            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="text-center" style={{ width: '60px' }}>Foto</th>
                                    <th>Chapa</th>
                                    <th>Nome</th>
                                    <th>Setor</th>
                                    <th>Função</th>
                                    <th>Início Férias</th>
                                    <th className="text-center">Status</th>
                                    <th>Observação</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: '0.85rem' }}>
                                {relatorioFerias.length > 0 ? (
                                    relatorioFerias.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="text-center py-1">
                                                <FotoMiniatura chapa={item.chapa} onFotoCarregada={handleFotoCarregada} />
                                            </td>
                                            <td>{item.chapa}</td>
                                            <td className="fw-bold">{item.nome}</td>
                                            <td>{item.setor}</td>
                                            <td>{item.funcao}</td>
                                            <td>{item.data_inicio_ferias}</td>
                                            <td className="text-center"><Badge bg="info">{item.status}</Badge></td>
                                            <td>{item.observacao || '-'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted py-4">
                                            Selecione o mês/ano e clique em <strong>Consultar</strong> para carregar o relatório.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-end gap-2">
                    <Button variant="dark" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1" onClick={handleImprimirFerias} disabled={loading || relatorioFerias.length === 0}>
                        <FaPrint /> Imprimir
                    </Button>
                    <Button variant="secondary" size="sm" className="px-3" onClick={() => setShowFeriasModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Footer />

            <Foto show={!!fotoSelecionada} onClose={() => setFotoSelecionada(null)} foto={fotoSelecionada?.foto} nome={fotoSelecionada?.nome} info01={fotoSelecionada?.loja} info02="" />

            <Modal show={loading} backdrop="static" keyboard={false} centered size="sm" style={{ zIndex: 1090 }} contentClassName="bg-transparent border-0 shadow-none">
                <Modal.Body className="text-center p-4 rounded" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
                    <Spinner animation="border" variant="light" style={{ width: '3rem', height: '3rem' }} />
                    <div className="text-light mt-3 fw-bold">Processando...</div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default AprovacaoComunicado;